import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import mapboxgl from 'mapbox-gl'
import { getTransactionAddress } from '@popety-io/utils'
import { Icon } from '../../atoms/Icon'
import { Badge } from '../../atoms/Badge'
import { LandAnalysisCard } from '../../organisms/LandAnalysisCard'
import { Loading } from '../../atoms/Loading'
import { TransactionDetailContentProps } from './TransactionDetail.types'
import { MAPBOX_TOKEN, LIGHT_MAPSTYLE } from '../../../config/constants'
import {
  TransactionInfoSection,
  TransactionInfoMainContainer,
  TransactionInfoContent,
  DetailTitle,
  DetailSubtitle,
  QuickSummaryChips,
  SummaryChip,
  TransactionMapContainer,
  DetailContent,
  DataGrid,
  DataGroup,
  DataSectionTitle,
  DataRow,
  DataLabel,
  DataValue,
  PriceValue,
  DescriptionBox,
  LoadingState,
  ErrorState,
  ParticipantsContainer,
  ParticipantGroup,
  ParticipantGroupHeader,
  ParticipantList,
  ParticipantCard,
  ParticipantType,
  ParticipantName,
  ParticipantDetails,
  PropertyCard,
  PropertyHeader,
  PropertyBadge,
  MapMarkerBadge,
  PropertyId,
  PropertyDetails,
  PropertyInfo,
  PropertyInfoRow,
  PropertyUnits,
  UnitsTitle,
  UnitItem,
  UnitType,
  UnitDetails,
  CollapsibleSection,
  CollapsibleHeader,
  CollapsibleContent,
  LinkedLandSection,
  LinkedLandDivider,
  LinkedLandLabel,
} from './TransactionDetail.styles'
import { PrintModeWrapper } from './TransactionDetailContent.styles'

// Set Mapbox access token
mapboxgl.accessToken = MAPBOX_TOKEN

// Helper to map transaction step to Badge variant
// Step label for the detail panel: the raw registry value, then the canonical
// `transaction_step_name_normalized`, then the translated `other`. The raw text is never shown.
// Same lookup order as the Explore table helper
// (apps/popety-io-app/src/pages/Explore/Transactions/utils/transactionLabels.ts).
const translatedStepLabel = (
  t: ReturnType<typeof useTranslation>['t'],
  key: string
): string | undefined => {
  const label = t(`transactions.transactionTypes.${key}`, { defaultValue: '' })
  return label || undefined
}

const getStepLabel = (
  t: ReturnType<typeof useTranslation>['t'],
  stepName?: string | null,
  normalizedStepName?: string | null
): string => {
  if (!stepName) return '—'
  return (
    translatedStepLabel(t, stepName) ??
    (normalizedStepName ? translatedStepLabel(t, normalizedStepName) : undefined) ??
    t('transactions.transactionTypes.other')
  )
}

const getStepBadgeVariant = (step: string): 'primary' | 'success' | 'warning' | 'secondary' => {
  switch (step?.toLowerCase()) {
    case 'achat':
      return 'primary'
    case 'vente':
      return 'success'
    case 'donation':
      return 'warning'
    default:
      return 'secondary'
  }
}

// Helper to map transaction step to SummaryChip variant
const getStepChipVariant = (
  step: string
): 'primary' | 'success' | 'warning' | 'info' | 'secondary' => {
  switch (step?.toLowerCase()) {
    case 'achat':
      return 'primary'
    case 'vente':
      return 'success'
    case 'donation':
      return 'warning'
    case 'echange':
      return 'info'
    default:
      return 'secondary'
  }
}

export const TransactionDetailContent: React.FC<TransactionDetailContentProps> = ({
  transaction,
  loading = false,
  printMode = false,
  resolvedLandsMap,
  landsLoading,
  onViewLand,
}) => {
  const { t, i18n } = useTranslation()
  const locale = i18n.language || 'de-CH'
  const [showRawData, setShowRawData] = useState(false)
  const [propertyMarkerMap, setPropertyMarkerMap] = useState<Map<number, number>>(new Map())
  const mapContainerRef = useRef<HTMLDivElement>(null)
  const mapRef = useRef<mapboxgl.Map | null>(null)

  // Refs for scroll targets
  const transactionInfoRef = useRef<HTMLDivElement>(null)
  const participantsRef = useRef<HTMLDivElement>(null)
  const propertiesRef = useRef<HTMLDivElement>(null)

  // Scroll to section handler
  const scrollToSection = (ref: React.RefObject<HTMLDivElement | null>) => {
    if (!printMode) {
      ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }

  // Initialize map for properties with locations
  useEffect(() => {
    // Add a small delay to ensure DOM is ready
    const timer = setTimeout(() => {
      if (
        !mapContainerRef.current ||
        !transaction?.transaction_properties ||
        transaction.transaction_properties.length === 0
      ) {
        return
      }

      // Clear any existing content
      if (mapContainerRef.current) {
        mapContainerRef.current.innerHTML = ''
      }

      if (!MAPBOX_TOKEN) {
        console.warn('Mapbox access token not configured')
        if (mapContainerRef.current) {
          mapContainerRef.current.innerHTML = `<div style="display: flex; align-items: center; justify-content: center; height: 100%; color: #888; font-size: 12px;">${t('transactions.detailPanel.mapTokenNotConfigured')}</div>`
        }
        return
      }

      // Extract and group coordinates from properties
      const propertyGroups = new Map<string, Array<{ property: any; index: number }>>()
      const coordinatesMap = new Map<string, [number, number]>()

      if (transaction.transaction_properties) {
        transaction.transaction_properties.forEach((property, index) => {
          if (property.land_geo_center) {
            let lon: number | null = null
            let lat: number | null = null

            if (property.land_geo_center.includes(',')) {
              const parts = property.land_geo_center.split(',').map((s) => s.trim())
              if (parts.length === 2) {
                lon = parseFloat(parts[0])
                lat = parseFloat(parts[1])
              }
            } else {
              const match = property.land_geo_center.match(
                /POINT\s*\(\s*([-\d.]+)\s+([-\d.]+)\s*\)/i
              )
              if (match) {
                lon = parseFloat(match[1])
                lat = parseFloat(match[2])
              }
            }

            if (lon !== null && lat !== null && !isNaN(lon) && !isNaN(lat)) {
              const coordKey = `${lon},${lat}`

              if (!propertyGroups.has(coordKey)) {
                propertyGroups.set(coordKey, [])
                coordinatesMap.set(coordKey, [lon, lat])
              }
              propertyGroups.get(coordKey)!.push({ property, index })
            }
          }
        })
      }

      const markerGroups = Array.from(propertyGroups.entries()).map(
        ([coordKey, properties], groupIndex) => ({
          coord: coordinatesMap.get(coordKey)!,
          properties,
          markerNumber: groupIndex + 1,
        })
      )

      const newPropertyMarkerMap = new Map<number, number>()
      markerGroups.forEach(({ properties, markerNumber }) => {
        properties.forEach(({ index }) => {
          newPropertyMarkerMap.set(index, markerNumber)
        })
      })
      setPropertyMarkerMap(newPropertyMarkerMap)

      let mapCenter: [number, number] = [8.2275, 46.8182]
      let zoom = 8

      if (markerGroups.length > 0) {
        mapCenter = markerGroups[0].coord
        zoom = markerGroups.length === 1 ? 14 : 12
      }

      try {
        const map = new mapboxgl.Map({
          container: mapContainerRef.current,
          style: LIGHT_MAPSTYLE,
          center: mapCenter,
          zoom: zoom,
          interactive: !printMode,
          attributionControl: false,
        })

        mapRef.current = map

        map.on('load', () => {
          if (markerGroups.length > 0) {
            markerGroups.forEach(({ coord, properties, markerNumber }) => {
              const el = document.createElement('div')
              el.className = 'property-marker'
              el.style.width = '28px'
              el.style.height = '28px'
              el.style.borderRadius = '50%'
              el.style.backgroundColor = '#2563eb'
              el.style.border = '3px solid white'
              el.style.boxShadow = '0 2px 8px rgba(0,0,0,0.3)'
              el.style.display = 'flex'
              el.style.alignItems = 'center'
              el.style.justifyContent = 'center'
              el.style.color = 'white'
              el.style.fontSize = '12px'
              el.style.fontWeight = 'bold'
              el.style.cursor = printMode ? 'default' : 'pointer'
              el.innerText = markerNumber.toString()

              if (!printMode) {
                const propertiesInfo = properties
                  .map(
                    ({ property, index }) => `
                  <div style="border-top: ${index > 0 ? '1px solid #eee' : 'none'}; padding-top: ${index > 0 ? '4px' : '0'}; margin-top: ${index > 0 ? '4px' : '0'};">
                    <strong>${t('transactions.detailPanel.mapPopup.propertyNumber', { index: index + 1 })}</strong><br/>
                    ${property.land_address || `${property.city_name || ''} ${property.npa_name || ''}`}<br/>
                    ${property.land_area ? t('transactions.detailPanel.mapPopup.land', { area: property.land_area }) : ''}
                    ${property.surface_area_total ? `<br/>${t('transactions.detailPanel.mapPopup.surface', { area: property.surface_area_total })}` : ''}
                  </div>
                `
                  )
                  .join('')

                const popup = new mapboxgl.Popup({ offset: 25 }).setHTML(`
                  <div style="padding: 8px; font-size: 12px;">
                    ${properties.length > 1 ? `<strong>${t('transactions.detailPanel.mapPopup.propertiesAtLocation', { count: properties.length })}</strong><br/><br/>` : ''}
                    ${propertiesInfo}
                  </div>
                `)

                new mapboxgl.Marker(el).setLngLat(coord).setPopup(popup).addTo(map)
              } else {
                new mapboxgl.Marker(el).setLngLat(coord).addTo(map)
              }
            })

            if (markerGroups.length > 1) {
              const bounds = new mapboxgl.LngLatBounds()
              markerGroups.forEach(({ coord }) => {
                bounds.extend(coord)
              })
              map.fitBounds(bounds, { padding: 50 })
            }
          }
        })
      } catch (error) {
        console.error('Failed to initialize map:', error)
      }
    }, 100)

    return () => {
      clearTimeout(timer)
      if (mapRef.current) {
        mapRef.current.remove()
        mapRef.current = null
      }
    }
  }, [transaction?.transaction_properties, printMode, t])

  const formatPrice = (price?: number) => {
    if (!price) return '—'
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: 'CHF',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price)
  }

  const formatArea = (area?: number) => {
    if (!area) return '—'
    return `${new Intl.NumberFormat(locale).format(area)} m²`
  }

  const formatDate = (dateString?: string) => {
    if (!dateString) return '—'
    try {
      return new Date(dateString).toLocaleDateString(locale, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    } catch {
      return '—'
    }
  }

  const getParticipantTypeLabel = (type?: string) => {
    const typeKeyMap: Record<string, string> = {
      individual: 'individual',
      company: 'company',
      estate: 'estate',
      non_profit: 'nonProfit',
      public_body: 'publicBody',
      other: 'other',
    }
    if (!type) return t('transactions.detailPanel.participantTypes.unknown')
    const key = typeKeyMap[type]
    return key ? t(`transactions.detailPanel.participantTypes.${key}`) : type
  }

  const getLegalTypeLabel = (type?: string) => {
    const typeKeyMap: Record<string, string> = {
      PPE: 'PPE',
      'B-F': 'BF',
      COP: 'COP',
      DDP: 'DDP',
      DP: 'DP',
    }
    if (!type) return type
    const key = typeKeyMap[type]
    return key ? t(`transactions.detailPanel.legalTypes.${key}`) : type
  }

  const getUnitTypeLabel = (type?: string) => {
    const typeKeyMap: Record<string, string> = {
      appartement: 'appartement',
      'Habitation à un seul logement': 'singleFamily',
      balcon: 'balcon',
      cave: 'cave',
      'Garage privé': 'privateGarage',
      loggia: 'loggia',
      box: 'box',
      garage: 'garage',
    }
    if (!type) return type
    const key = typeKeyMap[type]
    return key ? t(`transactions.detailPanel.unitTypes.${key}`) : type
  }

  const getPrimaryLocation = () => {
    if (!transaction) return '—'

    const address = getTransactionAddress(transaction)
    if (address?.type === 'address') return address.value
    if (address?.type === 'plotNumber') {
      return t('transactions.columns.parcel', { number: address.value })
    }
    return '—'
  }

  const buyers = transaction?.transaction_participants?.filter((p) => p.role === 'buyer') || []
  const sellers = transaction?.transaction_participants?.filter((p) => p.role === 'seller') || []

  if (loading) {
    return (
      <PrintModeWrapper $printMode={printMode}>
        <DetailContent>
          <LoadingState>{t('transactions.detailPanel.loading')}</LoadingState>
        </DetailContent>
      </PrintModeWrapper>
    )
  }

  if (!transaction) {
    return (
      <PrintModeWrapper $printMode={printMode}>
        <DetailContent>
          <ErrorState>{t('transactions.detailPanel.notFound')}</ErrorState>
        </DetailContent>
      </PrintModeWrapper>
    )
  }

  return (
    <PrintModeWrapper $printMode={printMode}>
      {/* Transaction Info Section - Title, Location, Metrics, Map */}
      <TransactionInfoSection>
        <TransactionInfoMainContainer>
          <TransactionInfoContent>
            <DetailTitle>
              <Icon name="file-text" size="small" />
              {transaction?.official_id ||
                t('transactions.detailPanel.transactionNumber', {
                  id: transaction?.id,
                }) ||
                t('transactions.detailPanel.title')}
              {transaction?.transaction_step_name && (
                <Badge
                  variant={getStepBadgeVariant(transaction.transaction_step_name)}
                  size="small"
                >
                  {getStepLabel(
                    t,
                    transaction.transaction_step_name,
                    transaction.transaction_step_name_normalized
                  )}
                </Badge>
              )}
            </DetailTitle>
            <DetailSubtitle>
              <Icon name="map-pin" size="small" />
              {getPrimaryLocation()}
              {transaction?.transaction_date && (
                <>
                  {' \u2022 '}
                  {formatDate(transaction.transaction_date)}
                </>
              )}
              <span style={{ color: 'white' }}>{transaction.id}</span>
            </DetailSubtitle>

            {/* Quick Summary Chips */}
            <QuickSummaryChips>
              {/* Transaction Step */}
              {transaction?.transaction_step_name && (
                <SummaryChip
                  $variant={getStepChipVariant(transaction.transaction_step_name)}
                  onClick={() => scrollToSection(transactionInfoRef)}
                >
                  <Icon name="file-text" size="small" />
                  {getStepLabel(
                    t,
                    transaction.transaction_step_name,
                    transaction.transaction_step_name_normalized
                  )}
                </SummaryChip>
              )}

              {/* Total Price */}
              {transaction?.transaction_total_price && (
                <SummaryChip $variant="primary" onClick={() => scrollToSection(transactionInfoRef)}>
                  <Icon name="dollar-sign" size="small" />
                  {formatPrice(transaction.transaction_total_price)}
                </SummaryChip>
              )}

              {/* Properties Count */}
              {(transaction?.properties_count || 0) > 0 && (
                <SummaryChip onClick={() => scrollToSection(propertiesRef)}>
                  <Icon name="building" size="small" />
                  {t('transactions.detailPanel.chips.properties', {
                    count: transaction?.properties_count || 0,
                  })}
                </SummaryChip>
              )}

              {/* Participants Count */}
              {(transaction?.buyers_count || 0) + (transaction?.sellers_count || 0) > 0 && (
                <SummaryChip onClick={() => scrollToSection(participantsRef)}>
                  <Icon name="users" size="small" />
                  {t('transactions.detailPanel.chips.participants', {
                    count: (transaction?.buyers_count || 0) + (transaction?.sellers_count || 0),
                  })}
                </SummaryChip>
              )}
            </QuickSummaryChips>
          </TransactionInfoContent>

          {/* Map for properties - on the right side (hidden in print mode) */}
          {!printMode &&
            transaction.transaction_properties &&
            transaction.transaction_properties.length > 0 && (
              <TransactionMapContainer ref={mapContainerRef}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                    color: '#888',
                    fontSize: '12px',
                  }}
                >
                  {t('transactions.detailPanel.loadingMap')}
                </div>
              </TransactionMapContainer>
            )}
        </TransactionInfoMainContainer>
      </TransactionInfoSection>

      <DetailContent>
        {/* Compact Transaction Information */}
        <DataGroup ref={transactionInfoRef}>
          <DataSectionTitle>
            <Icon name="info" size="small" />
            {t('transactions.detailPanel.sections.transactionDetails')}
          </DataSectionTitle>
          <DataGrid style={{ marginBottom: 0 }}>
            <div>
              <DataRow>
                <DataLabel>{t('transactions.detailPanel.labels.transactionId')}</DataLabel>
                <DataValue>{transaction.transaction_id || '—'}</DataValue>
              </DataRow>
              <DataRow>
                <DataLabel>{t('transactions.detailPanel.labels.officialId')}</DataLabel>
                <DataValue>{transaction.official_id || '—'}</DataValue>
              </DataRow>
              <DataRow>
                <DataLabel>{t('transactions.detailPanel.labels.totalPrice')}</DataLabel>
                <PriceValue>{formatPrice(transaction.transaction_total_price)}</PriceValue>
              </DataRow>
              {transaction.transaction_step_total_price && (
                <DataRow>
                  <DataLabel>{t('transactions.detailPanel.labels.stepPrice')}</DataLabel>
                  <DataValue>{formatPrice(transaction.transaction_step_total_price)}</DataValue>
                </DataRow>
              )}
            </div>
            <div>
              <DataRow>
                <DataLabel>{t('transactions.detailPanel.labels.transactionDate')}</DataLabel>
                <DataValue>{formatDate(transaction.transaction_date)}</DataValue>
              </DataRow>
              {transaction.publication_date && (
                <DataRow>
                  <DataLabel>{t('transactions.detailPanel.labels.publicationDate')}</DataLabel>
                  <DataValue>{formatDate(transaction.publication_date)}</DataValue>
                </DataRow>
              )}
              <DataRow>
                <DataLabel>{t('transactions.detailPanel.labels.language')}</DataLabel>
                <DataValue>{transaction.language || '—'}</DataValue>
              </DataRow>
            </div>
          </DataGrid>
        </DataGroup>

        {/* Participants Section - Side by side */}
        {(buyers.length > 0 || sellers.length > 0) && (
          <DataGroup ref={participantsRef}>
            <DataSectionTitle>
              <Icon name="users" size="small" />
              {t('transactions.detailPanel.sections.participants')}
            </DataSectionTitle>
            <ParticipantsContainer>
              {buyers.length > 0 && (
                <ParticipantGroup>
                  <ParticipantGroupHeader $role="buyer">
                    {t('transactions.detailPanel.labels.buyers')}
                    <span>{buyers.length}</span>
                  </ParticipantGroupHeader>
                  <ParticipantList>
                    {buyers.map((participant, index) => (
                      <ParticipantCard key={`buyer-${index}`}>
                        <ParticipantType>
                          {getParticipantTypeLabel(participant.type)}
                          {participant.legal_form && ` • ${participant.legal_form}`}
                        </ParticipantType>
                        <ParticipantName>{participant.full_name || '—'}</ParticipantName>
                        <ParticipantDetails>
                          {participant.address_street && (
                            <div>
                              {participant.address_street} {participant.address_number}
                              {participant.address_city && `, ${participant.address_city}`}
                              {participant.address_postal_code &&
                                ` ${participant.address_postal_code}`}
                            </div>
                          )}
                          {(participant.capacity || participant.share_text) && (
                            <div>
                              {participant.capacity &&
                                `${t('transactions.detailPanel.labels.capacity')}: ${participant.capacity}`}
                              {participant.capacity && participant.share_text && ' • '}
                              {participant.share_text &&
                                `${t('transactions.detailPanel.labels.share')}: ${participant.share_text}`}
                            </div>
                          )}
                        </ParticipantDetails>
                      </ParticipantCard>
                    ))}
                  </ParticipantList>
                </ParticipantGroup>
              )}

              {sellers.length > 0 && (
                <ParticipantGroup>
                  <ParticipantGroupHeader $role="seller">
                    {t('transactions.detailPanel.labels.sellers')}
                    <span>{sellers.length}</span>
                  </ParticipantGroupHeader>
                  <ParticipantList>
                    {sellers.map((participant, index) => (
                      <ParticipantCard key={`seller-${index}`}>
                        <ParticipantType>
                          {getParticipantTypeLabel(participant.type)}
                          {participant.legal_form && ` • ${participant.legal_form}`}
                        </ParticipantType>
                        <ParticipantName>{participant.full_name || '—'}</ParticipantName>
                        <ParticipantDetails>
                          {participant.address_street && (
                            <div>
                              {participant.address_street} {participant.address_number}
                              {participant.address_city && `, ${participant.address_city}`}
                              {participant.address_postal_code &&
                                ` ${participant.address_postal_code}`}
                            </div>
                          )}
                          {(participant.capacity || participant.share_text) && (
                            <div>
                              {participant.capacity &&
                                `${t('transactions.detailPanel.labels.capacity')}: ${participant.capacity}`}
                              {participant.capacity && participant.share_text && ' • '}
                              {participant.share_text &&
                                `${t('transactions.detailPanel.labels.share')}: ${participant.share_text}`}
                            </div>
                          )}
                          {participant.previous_buy_date && (
                            <div>
                              {t('transactions.detailPanel.labels.previous')}:{' '}
                              {formatDate(participant.previous_buy_date)}
                            </div>
                          )}
                        </ParticipantDetails>
                      </ParticipantCard>
                    ))}
                  </ParticipantList>
                </ParticipantGroup>
              )}
            </ParticipantsContainer>
          </DataGroup>
        )}

        {/* Properties Section - Compact */}
        {transaction.transaction_properties && transaction.transaction_properties.length > 0 && (
          <DataGroup ref={propertiesRef}>
            <DataSectionTitle>
              <Icon name="building" size="small" />
              {t('transactions.detailPanel.sections.propertiesCount', {
                count: transaction.transaction_properties.length,
              })}
            </DataSectionTitle>
            {transaction.transaction_properties.map((property, index) => {
              const markerNumber = propertyMarkerMap.get(index)

              return (
                <PropertyCard key={index}>
                  <PropertyHeader>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                      }}
                    >
                      {markerNumber !== undefined && (
                        <MapMarkerBadge>{markerNumber}</MapMarkerBadge>
                      )}
                      <PropertyBadge $type={property.legal_type}>
                        {getLegalTypeLabel(property.legal_type)}
                      </PropertyBadge>
                      <PropertyId>
                        {property.legal_id ||
                          t('transactions.detailPanel.labels.property', {
                            index: index + 1,
                          })}
                      </PropertyId>
                    </div>
                    {property.price && (
                      <PriceValue style={{ fontSize: '13px' }}>
                        {formatPrice(property.price)}
                      </PriceValue>
                    )}
                  </PropertyHeader>

                  <PropertyDetails>
                    <PropertyInfo>
                      <PropertyInfoRow>
                        <span>{t('transactions.detailPanel.labels.location')}</span>
                        <span>
                          {property.land_address ||
                            [property.city_name, property.npa_name].filter(Boolean).join(', ') ||
                            '—'}
                        </span>
                      </PropertyInfoRow>
                      <PropertyInfoRow>
                        <span>{t('transactions.detailPanel.labels.region')}</span>
                        <span>{property.region_name || '—'}</span>
                      </PropertyInfoRow>
                      {property.floor_nb && (
                        <PropertyInfoRow>
                          <span>{t('transactions.detailPanel.labels.floors')}</span>
                          <span>{property.floor_nb}</span>
                        </PropertyInfoRow>
                      )}
                    </PropertyInfo>
                    <PropertyInfo>
                      <PropertyInfoRow>
                        <span>{t('transactions.detailPanel.labels.landArea')}</span>
                        <span>{formatArea(property.land_area)}</span>
                      </PropertyInfoRow>
                      <PropertyInfoRow>
                        <span>{t('transactions.detailPanel.labels.totalSurface')}</span>
                        <span>{formatArea(property.surface_area_total)}</span>
                      </PropertyInfoRow>
                      {(property.property_share || property.share_thousands) && (
                        <PropertyInfoRow>
                          <span>{t('transactions.detailPanel.labels.share')}</span>
                          <span>{property.property_share || property.share_thousands || '—'}</span>
                        </PropertyInfoRow>
                      )}
                    </PropertyInfo>
                  </PropertyDetails>

                  {/* Units - Compact display */}
                  {property.units && property.units.length > 0 && (
                    <PropertyUnits>
                      <UnitsTitle>
                        {t('transactions.detailPanel.labels.unitsCount', {
                          count: property.units.length,
                        })}
                      </UnitsTitle>
                      {property.units.map((unit, unitIndex) => (
                        <UnitItem key={unitIndex}>
                          <UnitType>{getUnitTypeLabel(unit.unit_type)}</UnitType>
                          <UnitDetails>
                            {unit.unit_plan_number && <span>No{unit.unit_plan_number}</span>}
                            {unit.unit_area && <span>{formatArea(unit.unit_area)}</span>}
                            {unit.unit_room_count && <span>{unit.unit_room_count}R</span>}
                            {unit.unit_floor_label && <span>F{unit.unit_floor_label}</span>}
                          </UnitDetails>
                        </UnitItem>
                      ))}
                    </PropertyUnits>
                  )}

                  {/* Linked Land — inside property card */}
                  {property.land_id && (
                    <LinkedLandSection>
                      <LinkedLandDivider>
                        <LinkedLandLabel>
                          {t('transactions.detailPanel.sections.linkedLand')}
                        </LinkedLandLabel>
                      </LinkedLandDivider>
                      {landsLoading ? (
                        <Loading size="small" />
                      ) : resolvedLandsMap?.has(String(property.land_id)) ? (
                        <LandAnalysisCard
                          landAnalysis={resolvedLandsMap.get(String(property.land_id))!}
                          onView={onViewLand}
                          translations={{
                            view: t('transactions.detailPanel.labels.viewLandDetails'),
                            bldg: t('common.landCard.bldg'),
                            units: t('common.landCard.units'),
                          }}
                        />
                      ) : null}
                    </LinkedLandSection>
                  )}
                </PropertyCard>
              )
            })}
          </DataGroup>
        )}

        {/* Transaction Text */}
        {transaction.transaction_step_text && (
          <DataGroup>
            <DataSectionTitle>
              <Icon name="file-text" size="small" />
              {t('transactions.detailPanel.sections.transactionDescription')}
            </DataSectionTitle>
            <DescriptionBox>{transaction.transaction_step_text}</DescriptionBox>
          </DataGroup>
        )}

        {/* Raw Data - Collapsible (hidden in print mode) */}
        {transaction.transaction_raw_data && !printMode && (
          <CollapsibleSection $isOpen={showRawData}>
            <CollapsibleHeader onClick={() => setShowRawData(!showRawData)}>
              <span>
                <Icon name="code" size="small" style={{ marginRight: '6px' }} />
                {t('transactions.detailPanel.sections.rawTransactionData')}
              </span>
              <Icon name={showRawData ? 'arrow-up' : 'arrow-down'} size="small" />
            </CollapsibleHeader>
            <CollapsibleContent $isOpen={showRawData}>
              <DescriptionBox style={{ fontFamily: 'monospace', fontSize: '11px' }}>
                {transaction.transaction_raw_data}
              </DescriptionBox>
            </CollapsibleContent>
          </CollapsibleSection>
        )}
      </DetailContent>
    </PrintModeWrapper>
  )
}
